import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { diff } from 'deep-object-diff'
import { get, set } from 'local-storage'
import { useRouter } from 'next/router'
import queryString from 'query-string'

import { useProject } from './projectContext'

import { ENFORCE_STRING_KEYS } from '@/hooks/useRouterParams'
import {
  DEFAULT_PAGINATION_KEYS,
  stringifyParams,
  stringifyUrl,
} from '@/utils/params'

const LS_KEY = 'buildpass-query-params'

type LSValue = Record<string, Record<string, unknown>>

type Value<T extends object = Record<string, unknown>> = {
  params: Partial<T>
  /**
   * Merges the existing params with the provided ones. Will override any existing keys.
   */
  updateParams: (params: Partial<T>) => void
  /**
   * Replaces any existing params with the provided ones. To clear, pass an empty object.
   */
  setParams: (params: Partial<T>) => void
  /**
   * Wipes everything except the tab param if it exists
   */
  clearParams: () => void
  /**
   * Navigates (via `router.push()`) to the provided route with the provided params
   * @param url use `routes.<routeName>` to get the route
   * @param params the params to be added to the query string
   */
  pushWithParams: <K extends object = Record<string, unknown>>(
    url: string,
    params: Partial<K>
  ) => void
  /**
   * Returns a URL with the provided params added to the query string.
   * If there are params in the provided URL, they will be merged with the provided params.
   */
  hrefWithParams?: <K extends object = Record<string, unknown>>(
    url: string,
    params: Partial<K>
  ) => string
}

const QueryContext = createContext<Value>({
  params: {},
  updateParams: () => ({}),
  setParams: () => ({}),
  clearParams: () => ({}),
  isLoading: true,
  pushWithParams: () => ({}),
  hrefWithParams: () => null,
} as Value)

const areSame = (a: Record<string, unknown>, b: Record<string, unknown>) => {
  return Object.keys(diff(a, b)).length === 0
}

const getLsPathParams = (pathname: string) => {
  const lsParams: LSValue = get(LS_KEY) ?? {}
  return lsParams[pathname] ?? {}
}

const setLSPathParams = (pathname: string, params: Record<string, unknown>) => {
  const lsParams: LSValue = get(LS_KEY) ?? {}
  const newLsParams = { ...lsParams, [pathname]: params }
  set(LS_KEY, newLsParams)
}

const sanitiseObject = (obj: Record<string, unknown>) => {
  const newObj = { ...obj }
  Object.keys(newObj).forEach((key) => {
    const value = newObj[key]

    if (value === null) {
      delete newObj[key]
      return
    }

    if (value === undefined) {
      delete newObj[key]
      return
    }

    if (typeof value === 'string' && !value.length) {
      delete newObj[key]
      return
    }

    if (Array.isArray(value) && !value.length) {
      delete newObj[key]
      return
    }

    if (ENFORCE_STRING_KEYS.includes(key)) {
      newObj[key] = String(value)
      return
    }
  })
  return newObj
}

const pushHistory = (params: Record<string, string>) => {
  const search = stringifyParams(params)
  const text = search.length ? `?${search.toString()}` : ''
  const url = `${location.origin}${location.pathname}${text}`

  history.replaceState({ ...window.history.state, as: url, url }, '', url)
}

/**
 * ONLY EXPORTED FOR TESTING - DO NOT IMPORT THIS PLEASE
 */
export const getPathnameParams = (nextJSPathname: string) => {
  if (typeof global?.window?.location === 'undefined') return {}

  const search = location.search

  const queryStringParams = queryString.parse(search, {
    arrayFormat: 'bracket',
    parseBooleans: true,
    parseNumbers: true,
  })

  // if we have query string, then that's more important
  if (Object.keys(queryStringParams).length) {
    return sanitiseObject(queryStringParams)
  } else {
    // if nothing in query params, get from local storage
    const lsRouteParams = getLsPathParams(nextJSPathname)
    const lsParams = sanitiseObject(lsRouteParams)

    if (Object.keys(lsParams).length) {
      pushHistory(lsParams as Record<string, string>)
      return lsParams
    }
  }

  return {}
}

type Props = {
  children: React.ReactNode
}

export const QueryParamsProvider: React.FC<Props> = ({ children }) => {
  const memoProjectId = useRef<string>(null)

  const { project } = useProject() ?? {} // for public pages
  const { pathname, push } = useRouter()

  const [memoParams, setMemoParams] = useState<Record<string, unknown>>(
    getPathnameParams(pathname)
  )

  const updateParams = useCallback(
    (incommingParams: Record<string, unknown>) => {
      setMemoParams((currParams) => {
        const combinedParams = { ...currParams, ...incommingParams }
        const newParams = sanitiseObject(combinedParams)

        if (areSame(newParams, currParams)) {
          return currParams
        }

        // set the page back to 1 if filters change
        if (!incommingParams[DEFAULT_PAGINATION_KEYS.page]) {
          delete newParams[DEFAULT_PAGINATION_KEYS.page]
        }

        pushHistory(newParams as Record<string, string>)
        setLSPathParams(pathname, newParams)

        return newParams
      })
    },
    [pathname, setMemoParams]
  )

  const setParams = useCallback(
    (incommingParams: Record<string, unknown>) => {
      setMemoParams((currParams) => {
        const newParams = sanitiseObject(incommingParams)

        if (areSame(currParams, newParams)) {
          return currParams
        }

        setLSPathParams(pathname, newParams)
        pushHistory(newParams as Record<string, string>)

        return newParams
      })
    },
    [pathname, setMemoParams]
  )

  const clearParams = useCallback(() => {
    if (memoParams.tab) {
      setParams({ tab: memoParams.tab })
    } else {
      setParams({})
    }
  }, [memoParams.tab, setParams])

  const hrefWithParams = useCallback(
    <T extends Record<string, unknown>>(url: string, params: T) => {
      if (!Object.keys(params).length) return url
      return stringifyUrl(url, params)
    },
    []
  )

  const pushWithParams = useCallback(
    <T extends Record<string, unknown>>(url: string, params: T) => {
      push(hrefWithParams<T>(url, params))
    },
    [push, hrefWithParams]
  )

  const value = useMemo<Value>(() => {
    return {
      params: memoParams,
      updateParams,
      setParams,
      clearParams,
      pushWithParams,
      hrefWithParams,
    }
  }, [
    memoParams,
    updateParams,
    setParams,
    clearParams,
    pushWithParams,
    hrefWithParams,
  ])

  // listen for project change and if it's different, clear the page number
  useEffect(() => {
    if (!project?.id) return

    if (!memoProjectId.current) {
      memoProjectId.current = project.id
      return
    }

    if (memoProjectId.current !== project.id) {
      memoProjectId.current = project.id
      updateParams({ [DEFAULT_PAGINATION_KEYS.page]: null })
    }
  }, [project, updateParams])

  return <QueryContext.Provider value={value}>{children}</QueryContext.Provider>
}

type HookOptions<T extends Record<string, unknown>> = {
  clearOnRouteChange?: Array<keyof T> | Array<string>
}

export const useQueryParams = <T extends Record<string, unknown>>(
  options?: HookOptions<T>
) => {
  const { events } = useRouter()
  const queryParams = useContext(QueryContext) as Value<T>

  // if we have a clearOnRouteChange option, clear the params when the route changes
  useEffect(() => {
    const clearParams = () => {
      if (!options?.clearOnRouteChange?.length) return

      const paramsToClear = options.clearOnRouteChange as Array<keyof T>
      queryParams?.updateParams(
        paramsToClear.reduce((acc, key) => {
          acc[key] = null
          return acc
        }, {} as Partial<T>)
      )
    }

    if (options?.clearOnRouteChange?.length) {
      events.on('routeChangeStart', clearParams)
    }

    return () => {
      events.off('routeChangeStart', clearParams)
    }
  }, [events, options?.clearOnRouteChange, queryParams])

  return queryParams
}
